import type { Exporter, ExporterOptions } from './exporter';
import { msToUnixNano, nowMs, randomHex, tracesEndpoint, type Attributes, type SignalKind, type SpanRecord } from './otlp';
import { decideSessionSampled, pseudonymize, scrubMessage, SESSION_SAMPLE_RATE, shouldRecord } from './privacy';

interface LazyModule {
  createExporter(options: ExporterOptions): Exporter;
  startWebVitals(sink: (name: string, attributes: Attributes) => void): void;
}

export interface StartTelemetryOptions {
  /** `OTEL_COLLECTOR_URL` da config; ausente desliga tudo. */
  collectorUrl: string | null | undefined;
  serviceVersion: string;
  environment?: string;
  host?: string;
  sampleRate?: number;
  sessionSampled?: boolean;
  loader?: () => Promise<LazyModule>;
  idle?: (callback: () => void) => void;
}

export interface SpanHandle {
  readonly recording: boolean;
  readonly traceId: string;
  readonly spanId: string;
  end(attributes?: Attributes, status?: SpanRecord['status']): void;
}

type State = 'idle' | 'off' | 'on';

const PENDING_LIMIT = 200;
let state: State = 'idle';
let sessionSampled = false;
let exporter: Exporter | undefined;
let pending: SpanRecord[] = [];
let sessionId: string | undefined;
const context: Attributes = {};

const NOOP_SPAN: SpanHandle = { recording: false, traceId: '', spanId: '', end: () => undefined };

function commonAttributes(): Attributes {
  sessionId ??= randomHex(8);
  return { 'session.id': sessionId, ...context };
}

function canRecord(kind: SignalKind): boolean {
  if (state === 'off') return false;
  if (state === 'idle') return true;
  return shouldRecord(kind, sessionSampled);
}

function emit(span: SpanRecord) {
  if (exporter) {
    exporter.enqueue(span);
    return;
  }
  pending.push(span);
  if (pending.length > PENDING_LIMIT) pending.shift();
}

/** Abre um span; devolve um handle sem efeito quando o sinal não é amostrado. */
export function startSpan(kind: SignalKind, name: string, attributes: Attributes = {}, options: { client?: boolean } = {}): SpanHandle {
  if (!canRecord(kind)) return NOOP_SPAN;
  const traceId = randomHex(16);
  const spanId = randomHex(8);
  const start = nowMs();
  const base = { ...commonAttributes(), ...attributes };
  let ended = false;
  return {
    recording: true,
    traceId,
    spanId,
    end(extra = {}, status) {
      if (ended) return;
      ended = true;
      const end = nowMs();
      emit({
        kind,
        name,
        traceId,
        spanId,
        startTimeUnixNano: msToUnixNano(start),
        endTimeUnixNano: msToUnixNano(end),
        attributes: { ...base, ...extra, 'uyzy.duration_ms': Math.round(end - start) },
        ...(options.client ? { client: true } : {}),
        ...(status ? { status } : {}),
      });
    },
  };
}

/** Sinal instantâneo (web vital, ação de agente). */
export function recordEvent(kind: SignalKind, name: string, attributes: Attributes = {}) {
  startSpan(kind, name, attributes).end();
}

function describeError(error: unknown): { type: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return { type: error.name, message: error.message, ...(error.stack ? { stack: error.stack } : {}) };
  }
  return { type: typeof error, message: typeof error === 'string' ? error : 'non-error value' };
}

/** Erro de UI (boundary, handler global) em 100% das sessões, com mensagem limpa de PII. */
export function reportError(error: unknown, attributes: Attributes = {}) {
  if (!canRecord('error')) return;
  const { type, message, stack } = describeError(error);
  const clean = scrubMessage(message);
  const now = msToUnixNano(nowMs());
  emit({
    kind: 'error',
    name: 'exception',
    traceId: randomHex(16),
    spanId: randomHex(8),
    startTimeUnixNano: now,
    endTimeUnixNano: now,
    attributes: { ...commonAttributes(), ...attributes },
    status: { code: 'error', message: clean },
    events: [
      {
        name: 'exception',
        timeUnixNano: now,
        attributes: {
          'exception.type': type,
          'exception.message': clean,
          'exception.stacktrace': stack ? scrubMessage(stack, 4_000) : undefined,
        },
      },
    ],
  });
}

/** Troca de tela: rota como id do manifesto, nunca a URL (U30). */
export function recordNavigation(to: string, from: string | undefined, durationMs?: number) {
  recordEvent('navigation', 'navigation', { 'uyzy.route': to, 'uyzy.route.from': from, 'uyzy.navigation_ms': durationMs });
}

/** Trilha do agente (U24): ferramenta, origem e latência, nunca os argumentos. */
export function recordAgentAction(toolId: string, origin: string, outcome: 'ok' | 'denied' | 'error', latencyMs?: number) {
  recordEvent('agent', 'agent.tool', {
    'agent.tool_id': toolId,
    'agent.origin': origin,
    'agent.outcome': outcome,
    'agent.latency_ms': latencyMs,
  });
}

export function setTelemetryRoute(routeId: string | undefined) {
  context['uyzy.route'] = routeId;
}

/** Guarda só o hash do `sub`; `undefined` no logout. */
export async function setTelemetryUser(sub: string | undefined) {
  context['enduser.pseudo_id'] = sub ? await pseudonymize(sub) : undefined;
}

const IGNORED_ERRORS = [/ResizeObserver loop/];

function installGlobalHandlers() {
  if (typeof window === 'undefined') return;
  window.addEventListener('error', (event) => {
    const error: unknown = event.error ?? event.message;
    if (typeof event.message === 'string' && IGNORED_ERRORS.some((re) => re.test(event.message))) return;
    reportError(error, { 'error.source': 'window.error' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason, { 'error.source': 'unhandledrejection' });
  });
}

const defaultIdle = (callback: () => void) => {
  if (typeof globalThis.requestIdleCallback === 'function') globalThis.requestIdleCallback(callback, { timeout: 5_000 });
  else setTimeout(callback, 1);
};

/**
 * Liga a telemetria: sorteia a sessão, instala os handlers de erro e carrega o exportador em idle.
 * Sem `collectorUrl`, desliga e descarta o que estava pendente.
 */
export function startTelemetry(options: StartTelemetryOptions): Promise<void> {
  if (state === 'on') return Promise.resolve();
  if (!options.collectorUrl) {
    state = 'off';
    pending = [];
    return Promise.resolve();
  }
  state = 'on';
  sessionSampled = options.sessionSampled ?? decideSessionSampled(options.sampleRate ?? SESSION_SAMPLE_RATE);
  pending = pending.filter((span) => shouldRecord(span.kind, sessionSampled));
  installGlobalHandlers();

  const endpoint = tracesEndpoint(options.collectorUrl);
  const resource: Attributes = {
    'service.name': 'uyzy-web',
    'service.version': options.serviceVersion,
    'deployment.environment.name': options.environment,
    'uyzy.host': options.host,
    'telemetry.sdk.name': 'uyzy-otlp-lite',
    'telemetry.sdk.language': 'webjs',
  };
  const load = options.loader ?? (() => import('./lazy'));
  const idle = options.idle ?? defaultIdle;

  return new Promise((resolve) => {
    idle(() => {
      load()
        .then((lazy) => {
          exporter = lazy.createExporter({ endpoint, resource, autoFlush: typeof document !== 'undefined' });
          for (const span of pending) exporter.enqueue(span);
          pending = [];
          lazy.startWebVitals((name, attributes) => recordEvent('web-vital', name, attributes));
        })
        .catch(() => {
          state = 'off';
          pending = [];
        })
        .finally(resolve);
    });
  });
}

/** Descarrega a fila agora (logout, testes). */
export function flushTelemetry(useBeacon = false) {
  exporter?.flush(useBeacon);
}

/** Só para testes. */
export function resetTelemetryForTests() {
  exporter?.stop();
  exporter = undefined;
  state = 'idle';
  sessionSampled = false;
  pending = [];
  sessionId = undefined;
  for (const key of Object.keys(context)) Reflect.deleteProperty(context, key);
}
